import React from 'react';
import { Calendar, MapPin, Users, Flame, TrendingUp, Loader2, CheckCircle2, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

import FeedWidget from './FeedWidget';
import JournalWidget from './JournalWidget';

import { useUpcomingClasses } from '../../hooks/student/useUpcomingClasses';
import { useStudentKPIs } from '../../hooks/student/useStudentKPIs';
import { useMyEnrollments } from '../../hooks/schedule/useMyEnrollments';
import { ROUTES } from '../../constants/routes';

function formatTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function getInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?';
}

// ─── KPI Card (vertical, compact) ───────────────────────────────────────────

function KPICard({
  icon: Icon,
  label,
  value,
  accent = false,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
  accent?: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2.5">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${accent ? 'bg-[#9B1421]/20' : 'bg-white/5'}`}>
        <Icon className={`w-4 h-4 ${accent ? 'text-[#E8535E]' : 'text-[#A9A3A3]'}`} />
      </div>
      <div className="min-w-0">
        <p className="text-lg font-bold text-[#EBFFEE] leading-tight">{value}</p>
        <p className="text-[10px] text-[#A9A3A3]">{label}</p>
      </div>
    </div>
  );
}

// ─── Class Card (with instructor avatars) ───────────────────────────────────

function ClassCard({
  event,
  isEnrolled,
  isCheckedIn,
  onEnroll,
  isEnrolling,
}: {
  event: {
    event_id: string;
    sport_name: string | null;
    start_time: string | null;
    end_time: string | null;
    location_name: string | null;
    instructor_names: string[] | null;
    max_capacity: number | null;
    current_registrations: number | null;
    dateLabel: string;
  };
  isEnrolled: boolean;
  isCheckedIn: boolean;
  onEnroll: () => void;
  isEnrolling: boolean;
}) {
  const isFull =
    event.max_capacity !== null &&
    (event.current_registrations ?? 0) >= event.max_capacity;
  const instructors = event.instructor_names || [];

  return (
    <div className="p-3 hover:bg-white/[0.03] transition-colors">
      <div className="flex items-start gap-2.5">
        {/* Instructor avatar(s) */}
        <div className="shrink-0 flex -space-x-1.5 mt-0.5">
          {instructors.length > 0 ? (
            instructors.slice(0, 2).map((name, i) => (
              <div
                key={i}
                className="w-8 h-8 rounded-full bg-[#9B1421]/30 flex items-center justify-center text-xs font-semibold text-[#EBFFEE] border-2 border-[#221F1F]"
                title={name}
              >
                {getInitial(name)}
              </div>
            ))
          ) : (
            <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
              <Calendar className="w-3.5 h-3.5 text-[#A9A3A3]" />
            </div>
          )}
        </div>

        {/* Details */}
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-[#EBFFEE] truncate leading-tight">
            {event.sport_name ?? 'Class'}
          </p>
          <div className="flex items-center gap-1.5 text-[10px] text-[#A9A3A3] mt-1">
            <Clock className="w-2.5 h-2.5 shrink-0" />
            <span>{event.dateLabel} {formatTime(event.start_time)}</span>
          </div>
          <div className="flex items-center gap-2.5 text-[10px] text-[#A9A3A3] mt-0.5">
            {event.location_name && (
              <span className="flex items-center gap-0.5 truncate">
                <MapPin className="w-2.5 h-2.5 shrink-0" />
                {event.location_name}
              </span>
            )}
            {event.max_capacity !== null && (
              <span className="flex items-center gap-0.5 shrink-0">
                <Users className="w-2.5 h-2.5" />
                {event.current_registrations ?? 0}/{event.max_capacity}
              </span>
            )}
          </div>
        </div>

        {/* Status/Action */}
        <div className="shrink-0 mt-1">
          {isCheckedIn ? (
            <span className="flex items-center gap-1 text-[10px] text-green-400 bg-green-600/15 px-2 py-1 rounded-md">
              <CheckCircle2 className="w-3 h-3" />
              Done
            </span>
          ) : isEnrolled ? (
            <span className="text-[10px] text-[#EBFFEE] bg-[#9B1421]/30 px-2 py-1 rounded-md font-medium">
              Enrolled
            </span>
          ) : (
            <button
              onClick={onEnroll}
              disabled={isEnrolling || isFull}
              className="text-[10px] bg-[#9B1421] hover:bg-[#7A0F1A] text-white px-2.5 py-1 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isEnrolling ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : isFull ? (
                'Full'
              ) : (
                'Enroll'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Left Sidebar ───────────────────────────────────────────────────────────

function LeftSidebar({
  totalClasses,
  classesThisMonth,
  streak,
  kpisLoading,
}: {
  totalClasses: number;
  classesThisMonth: number;
  streak: number;
  kpisLoading: boolean;
}) {
  const { classes, isLoading } = useUpcomingClasses();
  const { enrolledClassIds, enrollmentByClassId, enroll, isEnrolling } = useMyEnrollments();

  return (
    <div className="space-y-4">
      {/* KPI Card */}
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-[#504A4A]/50">
          <h3 className="text-xs font-semibold text-[#A9A3A3] uppercase tracking-wider">Your Stats</h3>
        </div>
        <div className="divide-y divide-[#504A4A]/30">
          <KPICard icon={TrendingUp} label="Total Classes" value={kpisLoading ? '...' : totalClasses} />
          <KPICard icon={Calendar} label="This Month" value={kpisLoading ? '...' : classesThisMonth} />
          <KPICard icon={Flame} label="Week Streak" value={kpisLoading ? '...' : `${streak}w`} accent />
        </div>
      </div>

      {/* Upcoming Classes */}
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#504A4A]/50">
          <h3 className="text-xs font-semibold text-[#A9A3A3] uppercase tracking-wider">Upcoming</h3>
          <Link
            to={ROUTES.STUDENT_SCHEDULE}
            className="text-[11px] text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
          >
            View All
          </Link>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-[#A9A3A3]" />
          </div>
        ) : classes.length === 0 ? (
          <div className="p-4 text-center">
            <Calendar className="w-6 h-6 text-[#504A4A] mx-auto mb-1.5" />
            <p className="text-xs text-[#A9A3A3]">No upcoming classes</p>
            <Link
              to={ROUTES.STUDENT_SCHEDULE}
              className="text-[11px] text-[#E8535E] hover:text-[#EBFFEE] transition-colors mt-1 inline-block"
            >
              Browse schedule
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-[#504A4A]/30">
            {classes.slice(0, 5).map((cls) => {
              const enrollment = enrollmentByClassId[cls.event_id];
              return (
                <ClassCard
                  key={cls.event_id}
                  event={cls}
                  isEnrolled={enrolledClassIds.has(cls.event_id)}
                  isCheckedIn={enrollment?.attended === true}
                  onEnroll={() => enroll({ classId: cls.event_id })}
                  isEnrolling={isEnrolling}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Dashboard ─────────────────────────────────────────────────────────

export default function StudentHome() {
  const { totalClasses, classesThisMonth, streak, isLoading: kpisLoading } = useStudentKPIs();

  return (
    <div className="py-6">
      {/* 3-column: left sidebar | center feed | right sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 max-w-[1400px] mx-auto">
        {/* ── Left Sidebar (KPIs + Classes) ─────────────────────── */}
        <div className="hidden lg:block lg:col-span-3">
          <div className="sticky top-6 max-h-[calc(100vh-3rem)] overflow-y-auto [scrollbar-width:thin] [scrollbar-color:#504A4A_transparent]">
            <LeftSidebar
              totalClasses={totalClasses}
              classesThisMonth={classesThisMonth}
              streak={streak}
              kpisLoading={kpisLoading}
            />
          </div>
        </div>

        {/* ── Center Feed ───────────────────────────────────────── */}
        <div className="lg:col-span-6 space-y-4">
          <h1 className="text-2xl font-bold text-[#EBFFEE]">Feed</h1>
          <FeedWidget />
        </div>

        {/* ── Right Sidebar (Journal) ───────────────────────────── */}
        <div className="hidden lg:block lg:col-span-3">
          <div className="sticky top-6 max-h-[calc(100vh-3rem)] overflow-y-auto [scrollbar-width:thin] [scrollbar-color:#504A4A_transparent]">
            <JournalWidget />
          </div>
        </div>

        {/* ── Mobile: show sidebar content below feed ───────────── */}
        <div className="lg:hidden space-y-4">
          <LeftSidebar
            totalClasses={totalClasses}
            classesThisMonth={classesThisMonth}
            streak={streak}
            kpisLoading={kpisLoading}
          />
          <JournalWidget />
        </div>
      </div>
    </div>
  );
}
